/** Route slug ↔ `/legal/debt-financial-legal`. */
export const DEBT_FINANCIAL_LEGAL_SLUG = "debt-financial-legal";

export const debtFinancialLegalDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "1052", label: "Act 1052 lens" },
    { value: "ADR", label: "Settle smarter" },
    { value: "708", label: "Insolvency" },
  ],
  funnelTagline: "Educate · document · escalate lawfully—not through threats alone",
  focusAreas: ["Debt recovery", "Loan disputes", "Collateral & enforcement", "Insolvency & ADR"],
  primaryCtaHeading: "Speak to a financial lawyer",
  introduction: {
    headline: "WHY DEBT & FINANCIAL LEGAL ISSUES MATTER IN GHANA",
    lead:
      "Debt touches households, traders, SMEs, employers, lenders, and diaspora-remittance circles—usually surfacing as unpaid invoices, strained mobile-money loans, aggressive recovery calls, disputed collateral, and broken handshake deals.",
    problemStarts:
      "The legal fracture often begins before court: blurred contracts, oral-only terms you cannot prove, and repayments chased without reconciliation statements.",
    frameworkLead: "Ghana layers active statutes and regulators around this space:",
    frameworkBullets: [
      "Borrowers and Lenders Act, 2020 (Act 1052) plus Bank of Ghana implementation standards for many regulated credit relationships.",
      "Courts and ADR channels for structured recovery and settlement.",
      "Insolvency Act, 2006 (Act 708) when liabilities outrun assets.",
    ],
  },
  miniSiteGoals: {
    title: "QUESTIONS THIS HUB SHOULD ANSWER",
    subtitle: "High-intent users already inside a live default, invoice fight, threatened repossession, or collapsing cashflow:",
    questions: [
      "Can a lender seize my collateral immediately?",
      "What should I do if someone owes me money and refuses to pay?",
      "What if loan terms felt unclear or misleading?",
      "Can unfair charges or enforcement steps be challenged?",
      "What is the safest lawful recovery path?",
      "Court vs mediation—when does each make sense?",
      "What does insolvency-style relief entail in Ghana?",
      "Which documents matter before filings?",
    ],
  },
  frameworkDetail: {
    title: "MAIN LEGAL & REGULATORY FRAMEWORK",
    items: [
      {
        title: "Borrowers & Lenders Act, 2020 (Act 1052)",
        body: "Sets borrower/lender rights and lawful enforcement scaffolding for qualifying facilities—BoGh issues supporting directives.",
      },
      {
        title: "ADR Act, 2010 (Act 795)",
        body: "Arbitration, mediation, customary arbitration—for many sums this is quicker than years-long suits.",
      },
      {
        title: "High Court · District/Circuit Rules",
        body: "Civil filing fees, pleadings bundles, garnishees—jurisdictional ceilings dictate where claims start.",
      },
      {
        title: "Insolvency Act, 2006 (Act 708)",
        body: "Petition routes, Official Trustee involvement, High Court supervision when liquidity dies.",
      },
      {
        title: "Bank of Ghana consumer protection",
        body: "Licensed banks & SDIs must run complaint desks; unresolved cases can escalate to BoG’s consumer reporting office.",
      },
    ],
  },
  debtRecovery: {
    title: "WHAT DEBT RECOVERY REALLY MEANS",
    lead: "Recovery blends proof, patience, and lawful leverage—not only pressure.",
    channels: [
      "Demand letters & reconciliations",
      "Structured settlement & moratoria",
      "Mediation / arbitration clauses",
      "Civil suits & post-judgment enforcement",
      "Collateral realisation where permitted",
      "Insolvency petitions when entities implode",
    ],
    proofHeading: "Documents that win",
    proofBullets: [
      "Executed facility letters",
      "Invoices & delivery notes",
      "Repayment schedules & ledgers",
      "Admissions on chat or email",
      "Collateral schedules & perfection evidence",
      "Written demands with proof of service",
    ],
  },
  recoveryFlow: {
    title: "HOW LAWFUL RECOVERY USUALLY STARTS",
    steps: [
      "Confirm principal, interest, fees still contractually due",
      "Re-read covenants & events of default",
      "Issue written demand with reconciliation window",
      "Offer structured workout if cashflow is temporary",
      "Deploy ADR if relationship or sector norms favour speed",
      "Commence court or statutory enforcement when settlement fails",
    ],
    note: "ADR exists because many monetary fights settle once numbers are transparent—don’t default to litigation reflexively.",
  },
  courtRecovery: {
    title: "COURT-BASED RECOVERY",
    body:
      "Judicial Service guidance maps District, Circuit, and High Court monetary jurisdictions—exceed caps and your writ stalls. Complex or high-value commercial battles may channel through Commercial Court lists, and pilot Small Claims Debt Recovery benches are expanding access for modest sums.",
    signal:
      "Specialization signals how systemic unpaid-receivable problems have become—plan bundles, valuation, and experts before first mention dates.",
  },
  loanDisputes: {
    title: "WHY LOAN DISPUTES EXPLODE",
    disagreements: [
      "Was money truly disbursed?",
      "Contradictory memory on tenure or APR",
      "Lawfulness of penal fees",
      "Whether default formally occurred",
      "Whether enforcement complied with statutes",
      "Fair-treatment grievances versus contract strictness",
    ],
    ghanaFriction: [
      "Signing unread PDFs inside agent shops",
      "Verbally ‘explained’ side letters never annexed",
      "Hostile collectors skipping statements",
      "Half-paper trails on MoMo hybrids",
      "Informal rollover fees off ledger",
    ],
    bogNote:
      "Bank of Ghana warns about surprise charges, poor risk disclosure, buried Ts&Cs, and failure to emphasize harsh clauses—these map to actionable complaints, not silent suffering.",
  },
  borrowerChecklist: {
    title: "WHAT BORROWERS MUST VERIFY BEFORE SIGNING",
    items: [
      "Net disbursement versus headline loan",
      "Amortisation table vs bullet tricks",
      "Total cost of credit / APR equivalents",
      "Penalty & late interest maths",
      "Collateral description plus insurance duties",
      "Events of default & cure periods",
      "Permitted enforcement choreography",
      "Restructuring or hardship protocols",
      "Dispute-resolution forum (court vs arbitration seat)",
    ],
    warning:
      "If the officer cannot crisply narrate fees, collateral risk, or default timelines, walk away—or bring counsel before ink dries.",
  },
  collateralSection: {
    title: "COLLATERAL & ENFORCEMENT REALITY",
    truths: [
      "Security interests are seldom symbolic—they crystallize legal leverage once conditions are satisfied.",
      "Borrowers deserve clarity on haircut risk: vehicles, inventory, receivables, land-related charges—each class carries unique perfection steps.",
      "Lenders must still pursue statute-compliant processes; self-help grab-and-dash invites tort and regulatory heat.",
    ],
    balance:
      "Borrowers should stress-test worst-case loss; creditors should stress-test evidentiary files before threatening repossession.",
  },
  informalDebt: {
    title: "INFORMAL & RELATIONAL DEBT",
    pairs: [
      "Friends & family susu circles",
      "Supplier vs retailer trade credit",
      "Employer salary advances",
      "Landlord/tenant rental arrears",
      "Partner IOUs without SPVs",
    ],
    pain:
      "No paper, fuzzy due dates, emotional blackmail, and evidential silence on partial payments.",
    evidence:
      "Rescue paths: MoMo trails, bank SWIFT refs, WhatsApp admissions, witness affidavits, handwritten acknowledgments, partial settlement SMS.",
  },
  ifOwedMoney: {
    title: "IF SOMEONE OWES YOU MONEY",
    steps: [
      "Aggregate proof of principal + agreed interest",
      "Reconcile running balances with their records",
      "Confirm written vs oral variations",
      "Serve formal demand with cure period",
      "Avoid threats, stalking, or asset seizures outside law",
      "Mediate if cashflow is tight but good faith exists",
      "Issue writ / statutory notices when promises collapse",
    ],
    mantra: "Organized files beat loud voice notes—structure wins recoveries.",
  },
  ifPressured: {
    title: "IF YOU ARE BEING PRESSURED AS A BORROWER",
    moves: [
      "Demand amortisation statement & accrued charges",
      "Request copies of executed agreements",
      "Question penalties not mirrored in onboarding docs",
      "Refuse hurried side waivers absent counsel review",
      "Respond early—ghosting erodes goodwill & invites aggressive filings",
      "Licensed lender? Exhaust internal dispute desk, then BoG escalation with dated evidence",
    ],
  },
  consumerComplaints: {
    title: "CONSUMER COMPLAINTS (LICENSED INSTITUTIONS)",
    bogExamples: [
      "Unexpected/excessive tariff lines",
      "Loss tied to lethargic back-office workflows",
      "Account-drill disputes lacking investigation",
      "Marketing gloss hiding material risks",
      "Failure to prominently flag punitive clauses",
    ],
    takeaway:
      "Some ‘debt wars’ disguise as fairness fights—dual-track strategy: contractual defence + regulator-grade complaint dossiers.",
  },
  insolvencySection: {
    title: "INSOLVENCY (NOT ‘BANKRUPTCY’ CASUALLY)",
    lead:
      "Act 708 governs orderly wind-down/restructuring via petitions, Official Trustee interfaces, creditor committees, moratoria, and judicial oversight—not a DIY escape hatch.",
    when: [
      "Liabilities objectively exceed reachable assets",
      "Multiple concurrent enforcement threats",
      "Business cannot service obligations as they fall due",
      "Preemptive shields needed before rogue disposals",
    ],
    caution: "Serious reputational & director consequences—coordinate counsel before marketing insolvency casually online.",
  },
  adrSection: {
    title: "MEDIATION & ADR FOR MONEY FIGHTS",
    fit: [
      "Schedule disputes on supplier credit",
      "Partner drawdown quarrels",
      "Customer refund impasses",
      "Shareholder loan recalls",
      "Any matter where relationship economics beat scorched-earth trials",
    ],
    point: "Judicial Service actively steers ADR—use it to compress cost and preserve trade lines.",
  },
  commonProblems: {
    title: "COMMON PROBLEMS GHANAIANS FACE",
    items: [
      "Blind signing digital loans",
      "Handshake-only principal amounts",
      "Collateral pledged without insurance insight",
      "Opaque penalty stacking",
      "Collectors denying statements",
      "Private admissions vs public denials",
      "Family books merged with company ledgers",
      "Small receivables snowballing into existential defaults",
    ],
  },
  practicalTips: {
    title: "PRACTICAL TIPS",
    beforeBorrow: {
      heading: "Before borrowing",
      items: ["Read entire facility pack", "Model total cost—not teaser instalments", "Parse default & cure articles", "Map collateral exposure", "Never sign under duress"],
    },
    beforeLend: {
      heading: "Before private lending",
      items: ["Written term sheet", "Amount, purpose, tenor, schedule", "Witnesses or legalisation", "Traceable disbursement rails", "Capture acknowledgments"],
    },
    dispute: {
      heading: "When friction starts",
      items: ["Centralise documents", "Shift to writing", "Calendar mediation windows", "Lawyer before self-help"],
    },
    regulated: {
      heading: "If lender is regulated",
      items: ["Internal complaint first", "BoG escalation with exhibit index", "Archive every response letter"],
    },
  },
  redFlags: {
    title: "RED FLAGS",
    items: [
      "No written material terms",
      "Balances mysteriously shifting",
      "Surprise fee lines",
      "Pressure to pledge core home without counsel",
      "Sign-now-explain-later scripts",
      "Threats substituting for lawful process",
      "Refusal to issue statements",
      "Debtors dodging written acknowledgment",
      "Promises of illegal shortcuts to seize or vanish debt",
    ],
  },
  editorialPlaybook: {
    title: "HIGH-VALUE CONTENT CLUSTER",
    recoveryPages: ["Recover Money Owed in Ghana", "Debt Demand Letters Done Right", "When to Sue on an Unpaid Debt"],
    loanPages: ["Hidden Charges & Unfair Loan Terms", "Borrower Rights in Plain Language", "Life After Contractual Default"],
    collateralPages: ["Using Property as Security", "Can a Lender Repossess Quickly?", "Enforcement Reality Under Act 1052"],
    insolvencyPages: ["When Debt Becomes Unmanageable", "SME Liquidity SOS", "Before Creditors Lock Accounts"],
    adrPages: ["Mediation Pathways", "Safe Repayment Workouts", "Court vs ADR Decision Tree"],
    videos: ["Reconciliation labs", "BoG complaints walkthrough", "ADR convening demos"],
    leadCtasHeading: "Suggested CTAs",
    leadCtas: [
      "Recover my debt",
      "Review my loan agreement",
      "Challenge hidden charges",
      "Negotiate a repayment plan",
      "Protect my collateral",
      "Speak to a financial lawyer",
      "Get insolvency guidance",
      "Start mediation",
    ],
  },
  funnelApplication: {
    title: "INFOBOXX FUNNEL APPLICATION",
    hooksHeading: "Hooks",
    hooks: ["Someone owes me money and stalls.", "My lender threatens to grab collateral.", "These fees feel fabricated."],
    education: { label: "Education", text: "Proof stacks, borrower duties, collateral choreography, complaint ladders, court vs ADR." },
    authority: { label: "Authority", text: "Act 1052/BoGh, Act 795, civil procedure maps, Act 708, Judicial Service pilots." },
    conversion: {
      label: "Conversion",
      text: "Recovery retainers, contract forensics, BoG dossiers, enforcement strategy, mediation convening.",
    },
  },
  finalTakeaway: {
    title: "FINAL TAKEAWAY",
    pillars: ["Proof-first culture", "Regulator-aware bargaining", "Timely escalation", "No performative hostility"],
    messageLead: "Pain hits hardest when people:",
    mistakes: ["Wait years without ledgers", "Sign blind", "Rely on voice notes alone", "Confuse noise for lawful leverage"],
    safeMessage:
      "Strong financial legal bets are struck early—with documents surfaced and statutes actually read.",
  },
  bottomLine: {
    title: "BOTTOM LINE",
    lead: "Cash fights are procedural wars disguised as emotional ones.",
    smooth: "Documented reconciliation · lawful escalation",
    and: "vs",
    stressful: "Mystery balances · outlaw threats",
    closing: "Map proof, regulator rules, then pick court or ADR with counsel.",
  },
  faqs: [
    {
      q: "Can someone be jailed only for owing money?",
      a: "Ordinary debt disputes are civil. Coercive incarceration generally requires separate criminal contexts—not mere non-payment lacking fraud findings.",
    },
    {
      q: "Can lenders seize collateral overnight?",
      a: "Enforcement depends on contract, perfection, statutory windows, and sometimes court sanction—immediate grab rarely mirrors compliant process.",
    },
    {
      q: "What if nothing was formally signed?",
      a: "Claims or defences may still exist but evidence burden spikes—lean on traceable transfers, acknowledgments, and witness statements.",
    },
    {
      q: "Can I fight hidden bank charges?",
      a: "Yes—Bank of Ghana explicitly lists excessive or opaque fees, poor disclosure, and buried harsh clauses as complaint-worthy.",
    },
    {
      q: "They keep promising payment—nothing lands.",
      a: "That is precisely when mediated timelines or summons-backed pressure outperform endless calls.",
    },
  ],
  articles: [
    {
      id: "dfl-a-1",
      slug: "debt-recover-money-owed-ghana",
      title: "How to Recover Money Owed to You in Ghana",
      excerpt:
        "Threshold proof, reconciliation etiquette, lawful demand etiquette, mediation convening, and when to escalate to District vs High Court tiers.",
      type: "Guide",
      readTime: "11 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      updatedAt: "Updated recently",
      socialText: "Recover money owed in Ghana — lawful playbook",
      coverImage:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
      tags: ["Recovery", "Documents", "ADR"],
      pullQuote: "Ledgers—not anger—fuel garnishee success.",
      takeaways: ["Index every proof", "Model limitation periods", "Price ADR upfront"],
      video: {
        title: "Reconciliation labs",
        duration: "5:40",
        poster:
          "https://images.unsplash.com/photo-1563986768494-de4b277cdd52?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Before first demand",
          text: "",
          bullets: ["Outstanding principal math", "Accrued contractual interest caps", "Any forbearance letters"],
        },
      ],
    },
    {
      id: "dfl-a-2",
      slug: "debt-hidden-charges-borrower-rights-ghana",
      title: "Hidden Charges & Borrower Leverage",
      excerpt:
        "Act 1052 themes, BoG complaints matrices, refinancing traps, undue influence arguments, collateral release negotiations.",
      type: "Checklist",
      readTime: "9 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      updatedAt: "This week",
      socialText: "Challenge unfair lender charges using Ghana frameworks",
      coverImage:
        "https://images.unsplash.com/photo-1563986768494-de4b277cdd52?auto=format&fit=crop&w=800&q=80",
      tags: ["BoG", "Act 1052", "Consumer"],
      pullQuote: "Regulators reward documented complaint trails—not hallway gossip.",
      takeaways: ["Build exhibit binders", "Track SLA clocks", "Involve forensic accountants"],
      video: {
        title: "BoG complaints walkthrough",
        duration: "6:10",
        poster:
          "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      },
      body: [{ heading: "Parallel tracks", text: "Defend suit while regulator probes material disclosure breaches." }],
    },
    {
      id: "dfl-a-3",
      slug: "debt-insolvency-adr-options-ghana",
      title: "When Debts Spiral: Insolvency & ADR",
      excerpt:
        "Act 708 primer, Official Trustee interplay, creditor classes, mediation scripts for SMEs losing supplier trust.",
      type: "Expert Explainer",
      readTime: "10 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      updatedAt: "Reviewed this month",
      socialText: "Understand insolvency and ADR intersections in Ghana",
      coverImage:
        "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=800&q=80",
      tags: ["Act 708", "Mediation", "SME"],
      pullQuote: "Structured breathing room beats unmanaged fire sales.",
      takeaways: ["Audit director duties", "Map moratorium statutes", "Prepack settlements"],
      video: {
        title: "ADR convening demos",
        duration: "4:52",
        poster:
          "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80",
      },
      body: [{ heading: "SME lens", text: "Blend informal workouts with escrowed settlement payments." }],
    },
  ],
  videos: [
    {
      id: "dfl-v-1",
      title: "Reconciliation labs",
      duration: "5:40",
      poster:
        "https://images.unsplash.com/photo-1563986768494-de4b277cdd52?auto=format&fit=crop&w=800&q=80",
      description: "How to reconcile principal vs fees before demands fire.",
    },
    {
      id: "dfl-v-2",
      title: "BoG complaints walkthrough",
      duration: "6:10",
      poster:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      description: "Exhibit lists BoG reviewers expect.",
    },
    {
      id: "dfl-v-3",
      title: "ADR convening demos",
      duration: "4:52",
      poster:
        "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80",
      description: "Mediation openings that preserve counterparties.",
    },
  ],
  podcasts: [
    {
      id: "dfl-p-1",
      episodeLabel: "Episode 1",
      duration: "34 min",
      title: "Act 1052 for Non-Bankers",
      description: "What changed for everyday borrowers.",
      host: "Afrimore Legal Desk",
      publishedAt: "Jun 2026",
      coverImage:
        "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "dfl-p-2",
      episodeLabel: "Episode 2",
      duration: "30 min",
      title: "Small Claims Bench Pilots Explained",
      description: "Is your dispute ticket-sized?",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "May 2026",
      coverImage:
        "https://images.unsplash.com/photo-1563986768494-de4b277cdd52?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "dfl-p-3",
      episodeLabel: "Mini",
      duration: "18 min",
      title: "MoMo Receipts as Trial Exhibits",
      description: "Metadata hygiene for relational loans.",
      host: "Afrimore Legal Desk",
      publishedAt: "Apr 2026",
      coverImage:
        "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
